import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ProgressBar } from '@/components/ui/progress-bar';
import lureoAnimationWebm from '@/assets/Lureo_2.webm';
import lureoAnimationSafari from '@/assets/Lureo_2_safari.mov';
import { splashDone, splashProgress, splashStart } from './splashClock';

interface EditorLoadingScreenProps {
  isLoaded: boolean;
  sceneLoaded: boolean;
  isSceneReady: boolean;
  /** Org/subscription loaded so side menu has correct height before we hide splash */
  orgLoaded?: boolean;
  /** The editor's code has arrived: false only for the route's fallback, shown while it downloads */
  codeLoaded?: boolean;
}

const isSafariFamily = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  // All iOS browsers are Safari-family (WebKit) and don't support WebM reliably.
  const isIOS = /iphone|ipad|ipod/.test(ua);
  const isSafari =
    ua.includes('safari') && !ua.includes('chrome') && ua.includes('version/');
  return isIOS || isSafari;
};

export const EditorLoadingScreen: React.FC<EditorLoadingScreenProps> = ({
  isLoaded,
  sceneLoaded,
  isSceneReady,
  orgLoaded = true,
  codeLoaded = true
}) => {
  // This load's start, shared with the splash shown before this one (the route's fallback), so the animation carries on
  // rather than starting again
  const { key } = useLocation();
  const [startedAt] = useState(() => splashStart(key));
  // Gone the moment everything it waits for is in (org too, so the side menu and timeline are right from the first frame),
  // and never back for this load
  const ready = isLoaded && sceneLoaded && isSceneReady && orgLoaded;
  const [hidden, setHidden] = useState(ready);
  if (ready && !hidden) setHidden(true);
  useEffect(() => {
    if (hidden) splashDone();
  }, [hidden]);

  // Hide the initial HTML splash screen once React takes over
  useEffect(() => {
    const initialSplash = typeof document === 'undefined' ? null : document.getElementById('initial-splash');
    if (initialSplash) {
      initialSplash.classList.add('hidden');
    }
  }, []);

  if (hidden) {
    return null;
  }

  const videoSrc = isSafariFamily() ? lureoAnimationSafari : lureoAnimationWebm;
  
  return (
    <div
      data-editor-splash
      className="fixed inset-0 z-lureo-layer-blocker flex select-none flex-col items-center justify-center bg-lureo-emphasis-strong text-lureo-60 font-bold tracking-wider text-lureo-on-emphasis leading-none"
    >
      <video
        src={videoSrc}
        autoPlay
        muted
        playsInline
        // The animation from where this load's splash got to, not from the start
        onLoadedMetadata={(event) => {
          const video = event.currentTarget;
          const elapsed = (performance.now() - startedAt) / 1000;
          if (elapsed > 0.1 && Number.isFinite(video.duration)) video.currentTime = Math.min(elapsed, video.duration);
        }}
        className="pointer-events-none h-[28rem] w-auto"
      />
      <div className="-mt-12">
        {/* The real stages done, never a timer, so it shows where the load really is */}
        <ProgressBar maxValue={100} isFinishedLoading={ready} progress={splashProgress({ codeLoaded, isSceneReady, orgLoaded, sceneLoaded: isLoaded && sceneLoaded })} />
      </div>
    </div>
  );
};

